'use client';

import {
  rem,
  type ModalBaseStylesNames,
  type ModalProps,
  type Styles,
} from '@mantine/core';
import { openConfirmModal, openContextModal } from '@mantine/modals';
import type { ReactNode } from 'react';
import ConfirmModal from './confirm-modal';
import { EditPathInfoModal } from './edit-path-info-modal';
import GoodsInforModal from './goods-infor-modal';
import InsuranceItemModal from './insurance-item-modal/insurance-item';
import ItemTypeModal from './item-type-modal';
import CheckoutModal from './modal-checkout';
import CheckoutResultModal from './modal-checkout-result';
import ModalDndPath from './modal-dnd-path';
import InvalidServiceHourModal from './modal-invalid-service-hour';
import ModalMaxCodInfoDetail from './modal-max-cod-info-detail';
import OptimizeRouteModal from './modal-optimize-route';
import ModalPath from './modal-path';
import ModalSpecialRequestDetail from './modal-special-request-detail';
import NotEnoughBalanceModal from './not-enough-balance-modal';
import NoteModal from './note-modal/note-modal';
import NotiCashByRecipientModal from './noti-cash-by-recipient-modal';
import OnboardingPickupTimeModal from './onboarding-pickup-time-modal';
import PackageDetailsModal from './package-details-modal';
import BikeItemDescriptionsListModal from './package-details-modal/bike-item-description-list-modal';
import BikePackageSizeDefinitionModal from './package-details-modal/bike-package-size-definition-modal';
import TruckItemDescriptionsListModal from './package-details-modal/truck-item-description-list-modal';
import TruckPackageDetailsModal from './package-details-modal/truck-package-details-form';
import AddNewCardModal from './payment-modal/add-new-card-modal';
import LinkWalletModal from './payment-modal/link-wallet-modal';
import { PriceComparisonModal } from './price-comparison-modal/price-comparison-modal';
import ExceedDiscountLimitModal from './promotion-modal/component/exceed-discount-limit-modal';
import CreateOrderWithoutPromo from './promotion-modal/component/modal-create-order-without-promo';
import PromoDetail from './promotion-modal/component/promo-detail-modal';
import Promos from './promotion-modal/component/promos';
import { RatingDriverModal } from './rating-driver-modal';
import { FavoriteDriverConfirmModal } from './rating-driver-modal/favorite-driver-confirm-modal';
import MyRewardDetailModal from './reward-modal/my-reward-detail';
import MyRewardListModal from './reward-modal/my-reward-list';
import ServiceDetailsModal from './service-details-modal';
import LoadingGoodsModal from './special-request-modal/loading-goods-modal';
import ModalSendSmsToRecipientReject from './special-request-modal/modal-send-sms-to-recipient-reject';
import { TopupModal } from './topup-modal/topup-modal';
import TruckServiceDetailsModal from './truck-service-details-modal';
import ReimbursementFeeModal from '@/components/modals/reimbursement-fee-modal';
import { CancelOrderConfirmModal } from '@/features/records/components/cancel-order/cancel-order-confirm-modal';
import { CancelReasonListModal } from '@/features/records/components/cancel-order/cancel-reason-list-modal';
import { WarningOverCancelledOrderModal } from '@/features/records/components/cancel-order/warning-over-cancelled-order-modal';
import ModalCancelAutoRenew from '@/features/subscription/components/progresses/modal-cancel-auto-renew';
import useIsMobile from '@/lib/is-mobile/use-is-mobile';

export enum ModalName {
  PackageDetail = 'packageDetail',
  PackageType = 'packageType',
  DetailMaxCod = 'detailMaxCod',
  Path = 'path',
  DndPath = 'dndPath',
  SavedPlaces = 'savedPlaces',
  Promotion = 'promotion',
  PromotionDetail = 'promotionDetail',
  PackageDetails = 'packageDetails',
  TruckPackageDetails = 'truckPackageDetails',

  Confirm = 'confirm',
  ItemType = 'itemType',
  NotiCashByRecipient = 'NotiCashByRecipient',
  NotEnoughBalance = 'NotEnoughBalance',
  AddNewCard = 'AddNewCard',
  LinkWallet = 'LinkWallet',
  Note = 'Note',
  CreateOrderWithoutPromo = 'CreateOrderWithoutPromo',

  ServiceDetails = 'ServiceDetails',
  TruckServiceDetails = 'TruckServiceDetails',
  SpecialRequestDetail = 'SpecialRequestDetail',
  GoodsInfor = 'GoodsInfor',
  InsuranceItem = 'InsuranceItem',
  OptimizeRoute = 'OptimizeRoute',
  Checkout = 'Checkout',
  CancelAutoRenew = 'CancelAutoRenew',
  CheckoutResult = 'CheckoutResult',
  SendSmsToRecipientReject = 'SendSmsToRecipientReject',
  PackageSizeDefinition = 'PackageSizeDefinition',
  BikeItemDescriptionsList = 'BikeItemDescriptionsList',
  TruckItemDescriptionsList = 'TruckItemDescriptionsList',
  TransportationDocuments = 'TransportationDocuments',
  BillingDetails = 'BillingDetails',
  OnboardingPickupTime = 'OnboardingPickupTime',

  // reward modal
  MyRewardList = 'MyRewardList',
  MyRewardDetail = 'MyRewardDetail',
  // special request modal
  LoadingGoods = 'LoadingGoods',
  InvalidServiceHour = 'InvalidServiceHour',
  CancelOrderConfirm = 'CancelOrderConfirm',
  CancelReasonList = 'CancelReasonList',
  WarningOverCancelledOrder = 'WarningOverCancelledOrder',
  ReimbursementFee = 'ReimbursementFee',
  RatingDriver = 'RatingDriver',
  FavoriteDriverConfirm = 'FavoriteDriverConfirm',
  Topup = 'Topup',
  ExceedDiscountLimit = 'ExceedDiscountLimit',
  EditPathInfo = 'EditPathInfo',
  PriceComparison = 'PriceComparison',
}

export const modals = {
  [ModalName.Path]: ModalPath,
  [ModalName.DetailMaxCod]: ModalMaxCodInfoDetail,
  [ModalName.DndPath]: ModalDndPath,
  [ModalName.Promotion]: Promos,
  [ModalName.PromotionDetail]: PromoDetail,
  [ModalName.PackageDetails]: PackageDetailsModal,
  [ModalName.TruckPackageDetails]: TruckPackageDetailsModal,
  [ModalName.Confirm]: ConfirmModal,
  [ModalName.ItemType]: ItemTypeModal,
  [ModalName.NotiCashByRecipient]: NotiCashByRecipientModal,
  [ModalName.NotEnoughBalance]: NotEnoughBalanceModal,
  [ModalName.AddNewCard]: AddNewCardModal,
  [ModalName.LinkWallet]: LinkWalletModal,
  [ModalName.Note]: NoteModal,
  [ModalName.CreateOrderWithoutPromo]: CreateOrderWithoutPromo,

  [ModalName.ServiceDetails]: ServiceDetailsModal,
  [ModalName.TruckServiceDetails]: TruckServiceDetailsModal,
  [ModalName.SpecialRequestDetail]: ModalSpecialRequestDetail,
  [ModalName.GoodsInfor]: GoodsInforModal,
  [ModalName.InsuranceItem]: InsuranceItemModal,
  [ModalName.OptimizeRoute]: OptimizeRouteModal,
  [ModalName.Checkout]: CheckoutModal,
  [ModalName.CancelAutoRenew]: ModalCancelAutoRenew,
  [ModalName.CheckoutResult]: CheckoutResultModal,
  [ModalName.SendSmsToRecipientReject]: ModalSendSmsToRecipientReject,
  [ModalName.PackageSizeDefinition]: BikePackageSizeDefinitionModal,
  [ModalName.BikeItemDescriptionsList]: BikeItemDescriptionsListModal,
  [ModalName.TruckItemDescriptionsList]: TruckItemDescriptionsListModal,
  [ModalName.OnboardingPickupTime]: OnboardingPickupTimeModal,
  // promoDetail: PromoDetail,

  // reward modal
  [ModalName.MyRewardList]: MyRewardListModal,
  [ModalName.MyRewardDetail]: MyRewardDetailModal,
  [ModalName.LoadingGoods]: LoadingGoodsModal,
  [ModalName.InvalidServiceHour]: InvalidServiceHourModal,
  [ModalName.CancelOrderConfirm]: CancelOrderConfirmModal,
  [ModalName.CancelReasonList]: CancelReasonListModal,
  [ModalName.WarningOverCancelledOrder]: WarningOverCancelledOrderModal,
  [ModalName.ReimbursementFee]: ReimbursementFeeModal,
  [ModalName.RatingDriver]: RatingDriverModal,
  [ModalName.FavoriteDriverConfirm]: FavoriteDriverConfirmModal,
  [ModalName.Topup]: TopupModal,
  [ModalName.ExceedDiscountLimit]: ExceedDiscountLimitModal,
  [ModalName.EditPathInfo]: EditPathInfoModal,
  [ModalName.PriceComparison]: PriceComparisonModal,
};

declare module '@mantine/modals' {
  export interface MantineModalsOverride {
    modals: typeof modals;
  }
}

type ModalDisplayMode = 'modal' | 'drawer';

export function showModal<T extends Record<string, any>>(
  name: keyof typeof modals,
  innerProps?: T,
  modalSettings: Partial<Omit<ModalProps, 'opened'>> & {
    modalId?: string;
    mobileType?: ModalDisplayMode;
  } = {}
) {
  const {
    styles = {},
    mobileType = 'modal',
    ...restModalSettings
  } = modalSettings;

  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;
  const isDrawerMode = isMobile && mobileType === 'drawer';

  if (isDrawerMode) {
    // Mobile: Drawer
    openContextModal({
      modal: name as string,
      modalId: name as string,
      innerProps,
      ...restModalSettings,
      fullScreen: true,
      withCloseButton: false,
      transitionProps: {
        transition: 'slide-up',
        duration: 200,
      },
      styles: {
        content: {
          borderTopLeftRadius: '12px',
          borderTopRightRadius: '12px',
          marginTop: 'auto',
          height: 'fit-content',
          maxHeight: '95svh',
          backgroundColor: '#fff',
        },
        body: {
          padding: 0,
          background: '#fff',
        },
      },
    });
    return;
  }

  // Default: Modal (desktop)
  const headerStyles = 'header' in styles ? styles.header : {};
  const titleStyles = 'title' in styles ? styles.title : {};
  const bodyStyles = 'body' in styles ? styles.body : {};
  const contentStyles = 'content' in styles ? styles.content : {};

  // @ts-expect-error - styles is not typed
  const modalStyles: Styles<ModalBaseStylesNames> = (theme) => ({
    ...styles,
    header: { display: 'none', padding: 0, ...headerStyles },
    title: { display: 'none', padding: 0, ...titleStyles },
    body: {
      padding: 0,
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      ...bodyStyles,
    },
    content: {
      background: theme.colors.neutral[0],
      position: 'relative',
      ...contentStyles,
    },
  });

  openContextModal({
    modal: name as string,
    modalId: name as string,
    innerProps,
    ...restModalSettings,
    styles: modalStyles,
    closeOnClickOutside: false,
    withCloseButton: false,
    centered: true,
  });
}

export type OpenCustomConfirmModalProps = {
  title: ReactNode;
  children?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
};

export function openCustomConfirmModal({
  title,
  children,
  confirmLabel = 'Tôi đã hiểu',
  cancelLabel = 'Đóng',
}: OpenCustomConfirmModalProps) {
  openConfirmModal({
    centered: true,
    title,
    children,
    labels: {
      confirm: confirmLabel,
      cancel: cancelLabel,
    },
    cancelProps: {
      display: 'none',
    },
    confirmProps: {
      fullWidth: true,
      h: rem(48),
      size: 'md',
      w: `calc(100% + ${rem(16)})`,
      mx: rem(-8),
      fw: 700,
    },
    classNames: {
      body: 'space-y-6',
    },
    styles: {
      header: {
        padding: rem(24),
      },
      title: {
        fontSize: rem(18),
        lineHeight: rem(24),
        color: 'neutral.7',
      },
      close: {
        '--cb-icon-size': rem(24),
        height: rem(24),
        width: rem(24),
      },
      body: {
        paddingLeft: rem(24),
        paddingRight: rem(24),
        fontSize: rem(16),
        lineHeight: rem(24),
        fontWeight: 500,
        color: 'var(--mantine-color-neutral-6)',
        whiteSpace: 'pre-line',
      },
    },
  });
}
